import canvasManager from "./canvas-manager";

let movementTableData = null;
let activeJaw = "upper";

export const setMovementTableData = (data) => {
  movementTableData = data || null;
  // Default to whichever jaw has data
  if (movementTableData) {
    if (
      Array.isArray(movementTableData.upper) &&
      movementTableData.upper.length
    ) {
      activeJaw = "upper";
    } else if (
      Array.isArray(movementTableData.lower) &&
      movementTableData.lower.length
    ) {
      activeJaw = "lower";
    }
  }
  renderMovementTable();
  syncJawToggleButtons();
};

export const hasMovementTableData = () => {
  if (!movementTableData) return false;
  const u = Array.isArray(movementTableData.upper)
    ? movementTableData.upper.length
    : 0;
  const l = Array.isArray(movementTableData.lower)
    ? movementTableData.lower.length
    : 0;
  return u + l > 0;
};

const ROWS = [
  { label: "Extrusion(E)/Intrusion(I)", key: "E | I" },
  { label: "Translation  Buccal(B)/Lingual(L)", key: "B | L" },
  { label: "Translation  Mesial(M)/Distal(D)", key: "M | D" },
  { label: "Rotation  Mesial(M)/Distal(D)", key: "R" },
  { label: "Angulation  Mesial(M)/Distal(D)", key: "Ti" },
  { label: "Torque  Buccal(B)/Lingual(L)", key: "To" },
];

// Sort teeth in dental-chart order:
// - upper: 1x descending (18..11) then 2x ascending (21..28)
// - lower: 4x descending (48..41) then 3x ascending (31..38)
function sortTeeth(rows, jaw) {
  const rightQuad = jaw === "upper" ? 1 : 4;
  const leftQuad = jaw === "upper" ? 2 : 3;
  return rows.slice().sort((a, b) => {
    const aQ = Math.floor(a.tooth / 10);
    const bQ = Math.floor(b.tooth / 10);
    if (aQ === rightQuad && bQ === leftQuad) return -1;
    if (aQ === leftQuad && bQ === rightQuad) return 1;
    if (aQ === rightQuad) return b.tooth - a.tooth;
    return a.tooth - b.tooth;
  });
}

function formatValue(v) {
  if (v == null || v === "") return "0";
  return String(v);
}

function renderMovementTable() {
  const container = document.getElementById("movement-table-container");
  if (!container) return;

  if (!movementTableData) {
    container.innerHTML =
      '<p class="movement-empty">No movement data available.</p>';
    return;
  }

  const rawRows = Array.isArray(movementTableData[activeJaw])
    ? movementTableData[activeJaw]
    : [];

  if (!rawRows.length) {
    container.innerHTML = `<p class="movement-empty">No ${activeJaw} movement data available.</p>`;
    return;
  }

  const teeth = sortTeeth(rawRows, activeJaw);

  const headerCells = teeth
    .map((r) => `<th class="tooth-col">${r.tooth}</th>`)
    .join("");
  const headerRow = `<tr><th class="row-label"></th>${headerCells}</tr>`;

  const bodyRows = ROWS.map((row) => {
    const cells = teeth
      .map(
        (toothRow) =>
          `<td class="value-cell">${formatValue(toothRow[row.key])}</td>`,
      )
      .join("");
    return `<tr><th class="row-label">${row.label}</th>${cells}</tr>`;
  }).join("");

  container.innerHTML = `
    <table class="movement-table">
      <thead>${headerRow}</thead>
      <tbody>${bodyRows}</tbody>
    </table>
  `;
}

function syncJawToggleButtons() {
  document
    .querySelectorAll(".movement-jaw-toggle")
    .forEach((btn) => {
      btn.classList.toggle(
        "active",
        btn.dataset.jaw === activeJaw,
      );
    });
}

function setActiveJaw(jaw) {
  if (jaw !== "upper" && jaw !== "lower") return;
  activeJaw = jaw;
  renderMovementTable();
  syncJawToggleButtons();
}

// Drive a window-resize on every animation frame for `durationMs` so the
// three.js canvas keeps in step with the renderer-container's CSS max-height
// transition. After each resize we synchronously re-render every canvas:
// renderer.setSize() clears the WebGL buffer, and the main render rAF
// (registered at startup) fires before this tick each frame, so without an
// immediate re-paint the browser would composite an empty buffer and the
// models would flicker out for the duration of the animation.
function driveResizeDuringTransition(durationMs) {
  const start = performance.now();
  const tick = (now) => {
    window.dispatchEvent(new Event("resize"));
    canvasManager.getAll().forEach(({ renderer, scene, camera, overlayScene, overlayCamera }) => {
      if (!renderer || !scene || !camera) return;
      renderer.render(scene, camera);
      if (overlayScene && overlayCamera) {
        renderer.autoClear = false;
        renderer.render(overlayScene, overlayCamera);
        renderer.autoClear = true;
      }
    });
    if (now - start < durationMs) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

export function openMovementPanel() {
  const panel = document.getElementById("movement-panel");
  if (!panel) return;
  document.body.classList.add("movement-panel-open");
  panel.classList.add("open");
  panel.setAttribute("aria-hidden", "false");
  driveResizeDuringTransition(320);
}

export function closeMovementPanel() {
  const panel = document.getElementById("movement-panel");
  if (!panel) return;
  document.body.classList.remove("movement-panel-open");
  panel.classList.remove("open");
  panel.setAttribute("aria-hidden", "true");
  driveResizeDuringTransition(320);
}

export function isMovementPanelOpen() {
  const panel = document.getElementById("movement-panel");
  return !!panel && panel.classList.contains("open");
}

export function toggleMovementPanel() {
  if (isMovementPanelOpen()) {
    closeMovementPanel();
  } else {
    openMovementPanel();
  }
}

export function initMovementPanel() {
  const panel = document.getElementById("movement-panel");
  if (!panel) return;

  panel
    .querySelectorAll(".movement-jaw-toggle")
    .forEach((btn) => {
      btn.addEventListener("click", () => {
        setActiveJaw(btn.dataset.jaw);
      });
    });

  const closeBtn = panel.querySelector(".movement-panel-close");
  if (closeBtn) {
    closeBtn.addEventListener("click", () => closeMovementPanel());
  }

  syncJawToggleButtons();
  renderMovementTable();
}
